import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';

import { formatAudCurrency } from '~/utils/numberFormatters';

import CopyInclusionsPrefillBar from './CopyInclusionsPrefillBar';
import InclusionEditCard from './InclusionEditCard';
import { COMMON_INCLUSION_PRESETS } from './fields';
import { sumInclusionValue } from './inclusionsModel';
import type { PackageRoomRow } from './inclusionsModel';
import type { InclusionAsset, InclusionDraft } from './types';

interface Props {
  assets: Array<InclusionAsset>;
  currency: string;
  drafts: Array<InclusionDraft>;
  onCreateAsset: (name: string) => InclusionAsset;
  nights: Array<number>;
  onAddInclusion: (prefill?: { name?: string; type?: string }) => void;
  onCopyFromFirstOption?: () => void;
  onRemoveDraft: (id: string) => void;
  onUpdateDraft: (id: string, changes: Partial<InclusionDraft>) => void;
  rooms: Array<PackageRoomRow>;
  sourceOptionLabel: string;
  targetOptionLabel: string;
}

export default function InclusionsEditView({
  assets,
  currency,
  drafts,
  onCreateAsset,
  nights,
  onAddInclusion,
  onCopyFromFirstOption,
  onRemoveDraft,
  onUpdateDraft,
  rooms,
  sourceOptionLabel,
  targetOptionLabel,
}: Props) {
  return (
    <>
      {onCopyFromFirstOption && (
        <CopyInclusionsPrefillBar
          sourceOptionLabel={sourceOptionLabel}
          targetOptionLabel={targetOptionLabel}
          onApply={onCopyFromFirstOption}
        />
      )}

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
          Quick add inclusion
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {COMMON_INCLUSION_PRESETS.map((preset) => (
            <Chip
              key={preset.label}
              label={preset.label}
              size="small"
              onClick={() => onAddInclusion({ name: preset.label, type: preset.type })}
            />
          ))}
        </Stack>
      </Box>

      {drafts.length > 0 && (
        <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1.5, py: 1 }}>
          <Typography variant="body2">
            <strong>{formatAudCurrency(sumInclusionValue(drafts))}</strong> of inclusion value on the lead package
          </Typography>
        </Box>
      )}

      <Stack spacing={1.5}>
        {drafts.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No inclusions added yet.
          </Typography>
        )}
        {drafts.map((draft) => (
          <InclusionEditCard
            key={draft.id}
            assets={assets}
            currency={currency}
            draft={draft}
            onCreateAsset={onCreateAsset}
            onChange={(changes) => onUpdateDraft(draft.id, changes)}
            onRemove={() => onRemoveDraft(draft.id)}
            nights={nights}
            rooms={rooms}
          />
        ))}
      </Stack>

      <Box>
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => onAddInclusion()}>
          Add inclusion
        </Button>
      </Box>
    </>
  );
}
